// React
import {
  useRef,
  ReactNode,
  Children,
  isValidElement,
  useEffect,
  useState,
  useCallback,
  useLayoutEffect,
} from 'react';

// Components
import { Typography } from '@archieai/ui';

// Motion
import { motion, useInView, Variants } from 'motion/react';

// Utils
import { cn } from '../../../utils/cn';

// Types
type TypographyVariant =
  | 'display'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'small'
  | 'tiny'
  | 'x-small'
  | 'column-header'
  | 'tag';

type TextMaskRevealProps = {
  /** Content to reveal - strings are split by \n, React elements animate as single blocks */
  children: ReactNode;
  /** Typography variant to use for text styling */
  variant?: TypographyVariant;
  /** Stagger delay between each line reveal (in seconds) */
  staggerDelay?: number;
  /** Duration of each line's reveal animation (in seconds) */
  duration?: number;
  /** Custom easing curve for the animation */
  ease?: number[];
  /** Amount of element that must be visible before triggering (0-1) */
  threshold?: number;
  /** Whether animation should only trigger once */
  once?: boolean;
  /** Additional className for the container */
  className?: string;
  /** Additional className for each line wrapper */
  lineClassName?: string;
  /** Tag to use for the container */
  as?: 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span';
  /** Delay before starting animation (in seconds) */
  delay?: number;
  /** Start animation immediately on mount instead of waiting for inView */
  animateOnMount?: boolean;
  /** Split text by visual line breaks (how text wraps) instead of explicit \n */
  splitByVisualLines?: boolean;
};

/**
 * Hook to split text into visual lines based on how it wraps in the container
 */
const useVisualLines = (
  text: string,
  containerRef: React.RefObject<HTMLElement | null>,
  enabled: boolean
): string[] => {
  const [lines, setLines] = useState<string[]>([text]);

  const calculateLines = useCallback(() => {
    if (!enabled || !containerRef.current || !text) {
      setLines([text]);
      return;
    }

    const container = containerRef.current;
    const computedStyle = window.getComputedStyle(container);

    // Create a temporary measurement element that matches the container's text styles
    const measureEl = document.createElement('span');
    measureEl.style.cssText = `
      position: absolute;
      visibility: hidden;
      white-space: nowrap;
      font-family: ${computedStyle.fontFamily};
      font-size: ${computedStyle.fontSize};
      font-weight: ${computedStyle.fontWeight};
      letter-spacing: ${computedStyle.letterSpacing};
      line-height: ${computedStyle.lineHeight};
    `;
    document.body.appendChild(measureEl);

    const containerWidth = container.clientWidth;
    const words = text.split(' ');
    const resultLines: string[] = [];
    let currentLine = '';

    words.forEach((word, index) => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      measureEl.textContent = testLine;
      const testWidth = measureEl.offsetWidth;

      if (testWidth > containerWidth && currentLine) {
        resultLines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }

      // Push the last line
      if (index === words.length - 1 && currentLine) {
        resultLines.push(currentLine);
      }
    });

    document.body.removeChild(measureEl);
    setLines(resultLines.length > 0 ? resultLines : [text]);
  }, [text, containerRef, enabled]);

  // Use useLayoutEffect to measure before paint
  useLayoutEffect(() => {
    calculateLines();
  }, [calculateLines]);

  // Recalculate on resize
  useEffect(() => {
    if (!enabled) return;

    const handleResize = () => {
      calculateLines();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateLines, enabled]);

  return lines;
};

/**
 * TextMaskReveal - Animated text reveal component
 *
 * Reveals text line by line with a smooth mask animation.
 * - Strings with \n are split into multiple lines
 * - With splitByVisualLines, text is split based on how it wraps in the container
 * - Single strings or React elements animate as one block
 * - Uses Typography component for consistent font styling
 */
export const TextMaskReveal = ({
  children,
  variant,
  staggerDelay = 0.12,
  duration = 0.8,
  ease = [0.33, 1, 0.68, 1],
  threshold = 0.1,
  once = true,
  className,
  lineClassName,
  as: Tag = 'div',
  delay = 0,
  animateOnMount = false,
  splitByVisualLines = false,
}: TextMaskRevealProps) => {
  const containerRef = useRef<HTMLElement>(null);
  const isInView = useInView(containerRef, {
    amount: threshold,
    once,
  });

  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [isReady, setIsReady] = useState(!splitByVisualLines);

  // Get the raw text for visual line splitting
  const rawText =
    typeof children === 'string'
      ? children
      : Children.toArray(children)
          .filter((child) => typeof child === 'string')
          .join(' ');

  // Use the visual lines hook
  const visualLines = useVisualLines(rawText, containerRef, splitByVisualLines);

  // Mark as ready once visual lines are calculated
  useEffect(() => {
    if (splitByVisualLines && visualLines.length > 0 && visualLines[0] !== rawText) {
      setIsReady(true);
    } else if (splitByVisualLines && containerRef.current) {
      // Small delay to ensure layout is ready
      const timer = setTimeout(() => setIsReady(true), 50);
      return () => clearTimeout(timer);
    }
  }, [splitByVisualLines, visualLines, rawText]);

  // Effect for animateOnMount
  useEffect(() => {
    if (animateOnMount && isReady && !shouldAnimate) {
      const timer = setTimeout(() => {
        setShouldAnimate(true);
      }, delay * 1000);
      return () => clearTimeout(timer);
    }
  }, [animateOnMount, delay, shouldAnimate, isReady]);

  // Effect for scroll-based trigger
  useEffect(() => {
    if (!animateOnMount && isInView && isReady && !shouldAnimate) {
      const timer = setTimeout(() => {
        setShouldAnimate(true);
      }, delay * 1000);
      return () => clearTimeout(timer);
    }
  }, [animateOnMount, isInView, delay, shouldAnimate, isReady]);

  // Create variants for each line with proper stagger delay
  const getLineVariants = (index: number): Variants => ({
    hidden: {
      y: '100%',
    },
    visible: {
      y: '0%',
      transition: {
        duration,
        ease: ease as [number, number, number, number],
        delay: index * staggerDelay,
      },
    },
  });

  // Process children into lines
  const processChildren = (): { key: string; content: ReactNode }[] => {
    // If visual line splitting is enabled and we have text
    if (splitByVisualLines && rawText) {
      return visualLines.map((line, index) => ({
        key: `visual-line-${index}`,
        content: line,
      }));
    }

    const childrenArray = Children.toArray(children);

    // If single string with newlines, split into multiple lines
    if (childrenArray.length === 1 && typeof childrenArray[0] === 'string') {
      const text = childrenArray[0];
      if (text.includes('\n')) {
        return text.split('\n').map((line, index) => ({
          key: `line-${index}`,
          content: line.trim(),
        }));
      }
      // Single line of text - animate as one block
      return [{ key: 'line-0', content: text }];
    }

    // Multiple children or React elements - each is a separate line
    return childrenArray.map((child, index) => {
      if (typeof child === 'string') {
        return { key: `line-${index}`, content: child };
      }
      if (isValidElement(child)) {
        return { key: `element-${index}`, content: child };
      }
      return { key: `item-${index}`, content: child };
    });
  };

  const lines = processChildren();

  // Render content with or without Typography wrapper
  const renderContent = (content: ReactNode) => {
    if (variant && typeof content === 'string') {
      return (
        <Typography as="span" variant={variant} color="inherit">
          {content}
        </Typography>
      );
    }
    return content;
  };

  return (
    <Tag
      ref={containerRef as React.RefObject<HTMLDivElement>}
      className={className}
    >
      {lines.map(({ key, content }, index) => (
        <span key={key} className={cn('overflow-hidden block', lineClassName)}>
          <motion.span
            className="block will-change-transform"
            variants={getLineVariants(index)}
            initial="hidden"
            animate={shouldAnimate ? 'visible' : 'hidden'}
          >
            {renderContent(content)}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};
